import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, switchMap } from 'rxjs';

import { Tile } from 'app/shared/components/tile-list/models';
import { selectMultiUserRecentActivitiesIfLoaded } from 'app/state/multi-user-preferences/multi-user-preferences.selectors';
import { selectAppModeIfDefined } from 'app/state/settings/settings.selectors';
import { selectUserRecentActivityNumbers } from 'app/state/user-session/user-session.selectors';

import { Activity, isActivityDivider } from '../../models';

export interface ActivityListState {
    activities: Activity[];
}

const initialState: ActivityListState = {
    activities: [],
};
export const maxLengthRecentActivitiesShown = 6;

@Injectable()
export class ActivityListStore extends ComponentStore<ActivityListState> {
    private store = inject(Store);

    constructor() {
        super(initialState);
    }

    activities$ = this.select((state) => state.activities);

    recentActivityNumbers$ = this.store.pipe(
        selectAppModeIfDefined,
        switchMap((appMode) =>
            appMode === 'multi-user'
                ? this.store.pipe(selectMultiUserRecentActivitiesIfLoaded)
                : this.store.select(selectUserRecentActivityNumbers),
        ),
        map((recentActivityNumbers) => recentActivityNumbers.slice(0, maxLengthRecentActivitiesShown)),
    );

    tiles$: Observable<Tile[]> = combineLatest([this.activities$, this.recentActivityNumbers$]).pipe(
        map(([activities, recentActivityNumbers]) => {
            const recentActivities = activities.filter((activity) =>
                recentActivityNumbers.includes(activity.number),
            );

            if (recentActivities.length) {
                return [
                    ...recentActivities.map(mapActivityToTile('Recent')),
                    ...activities.map(mapActivityToTile('All')),
                ];
            }
            return [...activities.map(mapActivityToTile())];
        }),
    );

    updateActivities = this.updater((state, activities: Activity[]) => ({ ...state, activities }));
}

function mapActivityToTile(groupName?: string) {
    return (activity: Activity): Tile => ({
        groupName: groupName,
        name: activity.name,
        id: activity.number.toString(),
        isSeparator: isActivityDivider(activity),
        iconClass: '',
        iconText: activity.number.toString(),
        tags: undefined,
    });
}
